import { useState } from 'react';
import { useAuthStore } from '../../stores/authStore';
import { MACHINA_VERSION } from '@machina/core';

interface LoginPageProps {
  onSwitchToSignup: () => void;
  onSwitchToForgotPassword: () => void;
}

export function LoginPage({ onSwitchToSignup, onSwitchToForgotPassword }: LoginPageProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { signIn, signInWithGoogle, isLoading, error, clearError } = useAuthStore();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    if (!email || !password) return;

    await signIn(email, password);
  };

  const handleGoogleSignIn = async () => {
    clearError();
    await signInWithGoogle();
  };

  return (
    <div style={styles.container}>
      {/* Background pattern */}
      <div style={styles.backgroundPattern} />

      {/* Login card */}
      <div style={styles.card}>
        {/* Header ornament */}
        <div style={styles.ornamentTop}>
          <span style={styles.ornamentSymbol}>&#9876;</span>
        </div>

        {/* Title */}
        <h1 style={styles.title}>MACHINA</h1>
        <p style={styles.subtitle}>Virtual Tabletop Powered by The Deep</p>

        {/* Divider */}
        <div style={styles.divider}>
          <span style={styles.dividerDot}>&#8226;</span>
          <span style={styles.dividerLine} />
          <span style={styles.dividerDot}>&#8226;</span>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={styles.form}>
          {error && (
            <div style={styles.error}>
              {error}
            </div>
          )}

          <div style={styles.inputGroup}>
            <label style={styles.label}>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="adventurer@realm.com"
              style={styles.input}
              autoComplete="email"
              disabled={isLoading}
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Your secret phrase"
              style={styles.input}
              autoComplete="current-password"
              disabled={isLoading}
            />
          </div>

          <button
            type="submit"
            disabled={isLoading || !email || !password}
            style={{
              ...styles.button,
              opacity: isLoading || !email || !password ? 0.6 : 1,
              cursor: isLoading || !email || !password ? 'not-allowed' : 'pointer',
            }}
          >
            {isLoading ? 'Entering...' : 'Enter the Realm'}
          </button>

          {/* OAuth divider */}
          <div style={styles.oauthDivider}>
            <span style={styles.dividerLine} />
            <span style={styles.oauthDividerText}>or</span>
            <span style={styles.dividerLine} />
          </div>

          {/* Google sign-in */}
          <button
            type="button"
            onClick={handleGoogleSignIn}
            disabled={isLoading}
            style={{
              ...styles.googleButton,
              opacity: isLoading ? 0.6 : 1,
              cursor: isLoading ? 'not-allowed' : 'pointer',
            }}
          >
            <svg width="16" height="16" viewBox="0 0 18 18" style={{ marginRight: 8, opacity: 0.8 }}>
              <path fill="currentColor" d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 0 0 2.38-5.88c0-.57-.05-.66-.15-1.18z"/>
              <path fill="currentColor" d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2a4.8 4.8 0 0 1-7.18-2.54H1.83v2.07A8 8 0 0 0 8.98 17z"/>
              <path fill="currentColor" d="M4.5 10.52a4.8 4.8 0 0 1 0-3.04V5.41H1.83a8 8 0 0 0 0 7.18l2.67-2.07z"/>
              <path fill="currentColor" d="M8.98 3.58c1.16 0 2.23.4 3.06 1.2l2.3-2.3A8 8 0 0 0 1.83 5.4L4.5 7.49a4.77 4.77 0 0 1 4.48-3.9z"/>
            </svg>
            Sign in via Google
          </button>
        </form>

        {/* Links */}
        <div style={styles.links}>
          <button
            onClick={onSwitchToForgotPassword}
            style={styles.link}
            type="button"
          >
            Forgot password?
          </button>
        </div>

        {/* Bottom divider */}
        <div style={styles.divider}>
          <span style={styles.dividerLine} />
        </div>

        {/* Sign up prompt */}
        <p style={styles.signupPrompt}>
          New to Machina?{' '}
          <button
            onClick={onSwitchToSignup}
            style={styles.signupLink}
            type="button"
          >
            Create an account
          </button>
        </p>

        {/* Footer ornament */}
        <div style={styles.ornamentBottom}>
          <span style={styles.ornamentSymbol}>&#9830;</span>
        </div>
      </div>

      {/* Version */}
      <p style={styles.version}>v{MACHINA_VERSION}</p>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'var(--color-background)',
    padding: 'var(--spacing-lg)',
    position: 'relative',
    overflow: 'hidden',
  },

  backgroundPattern: {
    position: 'absolute',
    inset: 0,
    opacity: 0.03,
    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23d4af37' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
  },

  card: {
    width: '100%',
    maxWidth: '400px',
    backgroundColor: 'var(--color-surface)',
    borderRadius: 'var(--radius-lg)',
    border: '2px solid var(--color-accent)',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 60px rgba(212, 175, 55, 0.1)',
    padding: 'var(--spacing-xl)',
    position: 'relative',
  },

  ornamentTop: {
    position: 'absolute',
    top: '-12px',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: 'var(--color-surface)',
    padding: '0 var(--spacing-sm)',
  },

  ornamentBottom: {
    position: 'absolute',
    bottom: '-10px',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: 'var(--color-surface)',
    padding: '0 var(--spacing-sm)',
  },

  ornamentSymbol: {
    color: 'var(--color-accent)',
    fontSize: '20px',
  },

  title: {
    textAlign: 'center',
    fontSize: '2.5rem',
    fontWeight: 700,
    letterSpacing: '0.3em',
    color: 'var(--color-accent)',
    margin: 0,
    marginTop: 'var(--spacing-sm)',
    textShadow: '0 2px 10px rgba(212, 175, 55, 0.3)',
  },

  subtitle: {
    textAlign: 'center',
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-secondary)',
    margin: 0,
    marginTop: 'var(--spacing-xs)',
    letterSpacing: '0.1em',
  },

  divider: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'var(--spacing-sm)',
    margin: 'var(--spacing-lg) 0',
  },

  dividerLine: {
    flex: 1,
    height: '1px',
    backgroundColor: 'var(--color-border)',
  },

  dividerDot: {
    color: 'var(--color-accent)',
    fontSize: '8px',
  },

  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-md)',
  },

  error: {
    padding: 'var(--spacing-sm) var(--spacing-md)',
    backgroundColor: 'rgba(220, 53, 69, 0.15)',
    border: '1px solid var(--color-error)',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--color-error)',
    fontSize: 'var(--font-size-sm)',
    textAlign: 'center',
  },

  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-xs)',
  },

  label: {
    fontSize: 'var(--font-size-sm)',
    fontWeight: 500,
    color: 'var(--color-text-secondary)',
    letterSpacing: '0.05em',
  },

  input: {
    padding: 'var(--spacing-sm) var(--spacing-md)',
    backgroundColor: 'var(--color-background)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--color-text)',
    fontSize: 'var(--font-size-md)',
    outline: 'none',
    transition: 'border-color var(--transition-fast), box-shadow var(--transition-fast)',
  },

  button: {
    marginTop: 'var(--spacing-sm)',
    padding: 'var(--spacing-md)',
    background: 'linear-gradient(180deg, rgba(212, 175, 55, 0.3) 0%, rgba(160, 128, 48, 0.4) 100%)',
    border: '2px solid var(--color-accent)',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--color-accent)',
    fontSize: 'var(--font-size-md)',
    fontWeight: 600,
    letterSpacing: '0.1em',
    transition: 'all var(--transition-fast)',
  },

  oauthDivider: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-sm)',
    marginTop: 'var(--spacing-sm)',
  },

  oauthDividerText: {
    color: 'var(--color-text-secondary)',
    fontSize: 'var(--font-size-sm)',
    textTransform: 'lowercase',
  },

  googleButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 'var(--spacing-sm) var(--spacing-md)',
    backgroundColor: 'transparent',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--color-text-secondary)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 500,
    transition: 'all var(--transition-fast)',
    letterSpacing: '0.05em',
  },

  links: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: 'var(--spacing-md)',
  },

  link: {
    background: 'none',
    border: 'none',
    color: 'var(--color-text-secondary)',
    fontSize: 'var(--font-size-sm)',
    cursor: 'pointer',
    textDecoration: 'underline',
    textUnderlineOffset: '2px',
  },

  signupPrompt: {
    textAlign: 'center',
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-secondary)',
    margin: 0,
  },

  signupLink: {
    background: 'none',
    border: 'none',
    color: 'var(--color-accent)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 600,
    cursor: 'pointer',
    textDecoration: 'none',
  },

  version: {
    position: 'absolute',
    bottom: 'var(--spacing-md)',
    color: 'var(--color-text-muted)',
    fontSize: 'var(--font-size-xs)',
    margin: 0,
  },
};
